/**
 * Web-side wiring for the dependency index (PR2, plan §13).
 *
 * The `DependencyIndex` CLASS + the pure `extractRefs` / `buildDependencyIndex`
 * helpers live in `@dxf-ai-editor/drawing-model` (`dependencyGraph.ts`) so the
 * web incremental index and `command-engine` PURGE share ONE implementation
 * (priority #1 — no second copy of CAD semantics). This module is the THIN
 * bridge that:
 *
 *   - rebuilds a holder's index from a whole `DrawingDocument` (non-streaming),
 *   - rebuilds a holder's index for a STREAMING-imported document by STREAMING
 *     each streamed space's entities from the compact store one at a time
 *     (the document's `space.entities[]` are discarded post-stream) and merging
 *     them with the block-definition edges (which the document always retains),
 *   - translates a `commitMutation` `EntityMutation` into a `DependencyBatch`.
 *
 * INDEX-OVER-EXISTING (D5): no `DrawingEntity` is allocated beyond the ONE the
 * compact-store generator materialises per step of the streaming walk (the
 * generator is `bulkLoad`-iterated once and never retained); the index stores
 * only `EntityRef{container,id,kind}` records. The streamed branch deliberately
 * STREAMS rather than snapshots — a full per-space `DrawingEntity[]` would be a
 * second full copy of the model space coexisting at peak with the compact cache.
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md §13.
 */

import {
  blockDependencyContainersFromDocument,
  blockRecordHandlesToNameFromDocument,
  type BlockChildEntity,
  type DependencyBatch,
  type DependencyContainer,
  type DependencyContainerKind,
  type DrawingDocument,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  iterateLiveEntitiesInSpace,
  type DocumentMemoryHolder,
} from "./documentMemoryHolder";
import type { EntityMutation } from "./types";

// Re-export the core surface so consumers can import the class + types from the
// web state barrel without reaching into drawing-model directly.
export {
  DependencyIndex,
  extractRefs,
  entityRefKey,
  buildDependencyIndex,
  type DependencyRefKind,
  type DependencyContainerKind,
  type DependencyIndexOptions,
  type EntityRef,
  type ExtractedRefs,
  type DependencyContainer,
  type DependencyBatch,
} from "@dxf-ai-editor/drawing-model";

/**
 * Rebuild `holder.dependencyIndex` from a whole document. For the NON-streaming
 * (whole-document) import path the document still carries every `space.entities`
 * — feed spaces AND block defs straight in. For a STREAMING import the spaces
 * are empty post-stream, so the streamed spaces' entities are STREAMED from the
 * compact store (one entity at a time via `iterateLiveEntitiesInSpace` — no full
 * per-space snapshot array) and merged with the (always-retained) block-def
 * edges.
 *
 * `streamedSpaceIds` is the holder's set of spaces whose inline entities were
 * discarded by the streaming path. A space in that set is read from the compact
 * store; a space NOT in it (legacy/bulk inline import) is read from the document
 * directly. This makes the one helper correct on BOTH paths — call it right
 * after `populateCompactCacheFromDocument*` / `markCachePopulated`.
 *
 * KNOWN LIMITATION (DEFER — out of PR2 §13 scope): for STREAMED spaces the
 * entities are rematerialised via `iterateLiveEntitiesInSpace`, which goes
 * through the SAME compact-store reconstructors as the old snapshot walk.
 * `dimensionFromCompact`
 * (compactStores/compactAnnotationStores.ts) reconstructs every Dimension with
 * `styleOverrides: {}` because the compact DIMENSION store does NOT persist
 * per-dimension DIMVAR overrides on the way IN either (`appendDimension` writes
 * no dimblk / styleOverride columns — see its "Opt 2b" note). Consequently a
 * STREAMED dimension that references an arrowhead BLOCK via
 * `styleOverrides.dimblk`/`dimblk1`/`dimblk2` is NOT recorded in
 * `getDependents("block", …)`, so such a block could be reported purgeable /
 * renamable while a streamed dim still points at it. Fixing this requires a
 * compact-store SCHEMA change (new columns + byte layout) which affects the
 * 685k-entity memory profile — a separate subsystem, deliberately NOT expanded
 * here. Non-streamed (inline / `buildDependencyIndex`) dimensions index their
 * dimblk* refs correctly (extractRefs reads the live `styleOverrides`). Tracked
 * for the compact-store DIMVAR-persistence follow-up (plan §13 / Codex P2 #217).
 */
export function rebuildHolderDependencyIndex(
  holder: DocumentMemoryHolder,
  document: DrawingDocument,
): void {
  const containers: DependencyContainer[] = [];
  for (const space of document.spaces) {
    if (holder.streamedSpaceIds.has(space.id)) {
      // Streamed: inline entities discarded — STREAM them from the compact
      // store one at a time via a generator. `bulkLoad` iterates this exactly
      // once (extractRefs → tiny EntityRef, never the entity), so no full
      // per-space `DrawingEntity[]` snapshot is materialised — the prior
      // `snapshotSpaceEntitiesFromHolder` allocated a SECOND full copy of the
      // entire model space (~150–300 MB on 685k-entity / КЖ drawings) that
      // coexisted at peak with the already-populated compact cache (the doubling
      // that OOM'd 204 MB DXFs). `iterateLiveEntitiesInSpace` yields the
      // IDENTICAL entity set (same iteration order, tombstone filter, ATTDEF
      // skip — kept in lockstep with the snapshot) so the resulting EntityRef
      // set is unchanged; this mirrors the metadata path's streaming walk
      // (`rebuildEntityMetadataIndexFromImportAsync` → `iterateSpaceEntity`).
      // INDEX-OVER-EXISTING (plan §5.1 D5): O(1) peak entity memory.
      containers.push({
        container: space.id,
        kind: "space",
        entities: iterateLiveEntitiesInSpace(holder, space.id),
      });
    } else {
      // Inline (bulk / legacy): the document array is the full-fidelity source.
      containers.push({
        container: space.id,
        kind: "space",
        entities: space.entities,
      });
    }
  }
  // Block-definition edges — `document.blocks` is ALWAYS retained (block
  // children are never streamed-discarded), so read them from the document.
  for (const c of blockDependencyContainersFromDocument(document)) {
    containers.push(c);
  }
  // R13 — the BLOCK_RECORD handle → block NAME map (from `document.blockRecords`,
  // always retained) so an INLINE dimension's `dimblk*Handle` imported soft-pointer
  // records a dependent on the arrow block it targets. (STREAMED dims still drop
  // styleOverrides in the compact store — the KNOWN LIMITATION above — so their
  // dimblk*/dimblk*Handle refs are not indexed either way.)
  holder.dependencyIndex.bulkLoad(
    containers,
    blockRecordHandlesToNameFromDocument(document),
  );
}

/**
 * Diff the block DEFINITIONS of `prevDocument` → `nextDocument` into a
 * `DependencyBatch` (container = block name). Block defs are mutated OUTSIDE
 * commitMutation's space-diff choke point — BEDIT/BSAVE/REFEDIT, AND ordinary
 * geometry commands issued while a block-edit session is active, pipe through
 * `syncBlockEditSpaceToDefinition` which rewrites `blocks[name].entities`
 * (returning a new `blocks` object). Feeding the result through the SAME
 * `applyBatch` the space path uses keeps removal exact-by-`(container,id)`: a
 * block whose name collides with a space id (e.g. a user block literally named
 * `model`, the model-space id) only ever removes that block child's key, never
 * the space entity's (PR2 Codex P2 #217). Only blocks whose entity array
 * actually changed are walked (reference check first), so a refresh never scans
 * the 685k-entity spaces (Codex P3 #217).
 */
export function blockDefDependencyBatch(
  prevDocument: DrawingDocument,
  nextDocument: DrawingDocument,
): DependencyBatch {
  const added: Array<{
    container: string;
    kind: DependencyContainerKind;
    entity: DrawingEntity | BlockChildEntity;
  }> = [];
  const removed: Array<{
    container: string;
    kind: DependencyContainerKind;
    id: string;
  }> = [];
  const updated: Array<{
    container: string;
    kind: DependencyContainerKind;
    prev: DrawingEntity | BlockChildEntity;
    next: DrawingEntity | BlockChildEntity;
  }> = [];
  const prevBlocks = prevDocument.blocks;
  const nextBlocks = nextDocument.blocks;
  const names = new Set<string>([
    ...Object.keys(prevBlocks),
    ...Object.keys(nextBlocks),
  ]);
  for (const name of names) {
    const pb = prevBlocks[name];
    const nb = nextBlocks[name];
    if (pb === nb) continue; // identical reference (incl. both absent) → skip
    const prevEntities = pb?.entities ?? [];
    const nextEntities = nb?.entities ?? [];
    if (prevEntities === nextEntities) continue;
    const prevById = new Map(prevEntities.map((e) => [e.id, e] as const));
    const nextById = new Map(nextEntities.map((e) => [e.id, e] as const));
    for (const e of nextEntities) {
      const p = prevById.get(e.id);
      if (p === undefined) {
        added.push({ container: name, kind: "block", entity: e });
      } else if (p !== e) {
        updated.push({ container: name, kind: "block", prev: p, next: e });
      }
    }
    for (const e of prevEntities) {
      if (!nextById.has(e.id))
        removed.push({ container: name, kind: "block", id: e.id });
    }
  }
  return { added, removed, updated };
}

/**
 * Re-sync the holder index's block-DEFINITION containers from a block-def
 * change (BEDIT/BSAVE/REFEDIT or an ordinary edit during a block-edit session).
 * Computes a precise prev→next diff (only changed blocks) and applies it
 * through the same `applyBatch` the space path uses — collision-safe, no space
 * scan (PR2 Codex P2/P3 #217).
 */
export function refreshBlockDefDependencyContainers(
  holder: DocumentMemoryHolder,
  prevDocument: DrawingDocument,
  nextDocument: DrawingDocument,
): void {
  holder.dependencyIndex.applyBatch(
    blockDefDependencyBatch(prevDocument, nextDocument),
  );
}

/**
 * Translate a `commitMutation` `EntityMutation` into a `DependencyBatch`. The
 * mutation's `spaceId` is the dependency `container` for top-level entities.
 *
 * NOTE: `commitMutation` only declares mutations for ENTITIES living in spaces
 * (added/removed/updated carry `spaceId`), so this batch only ever touches
 * space containers — matching how the metadata index is updated. removed is
 * id-only → delete by (container=spaceId, id). Block-DEFINITION children are
 * mutated on a SEPARATE path (BEDIT/BSAVE/REFEDIT pipe through
 * `syncBlockEditSpaceToDefinition`, rewriting `blocks[name].entities` outside
 * this choke point); those block-def containers are re-synced by
 * `refreshBlockDefDependencyContainers` — a prev→next diff fed through the same
 * `applyBatch` — after such a command (PR2 Codex P2/P3 #217).
 */
export function dependencyBatchFromMutation(
  mutations: EntityMutation,
): DependencyBatch {
  return {
    added: mutations.added.map((m) => ({
      container: m.spaceId,
      kind: "space" as const,
      entity: m.entity as DrawingEntity,
    })),
    removed: mutations.removed.map((m) => ({
      container: m.spaceId,
      kind: "space" as const,
      id: m.entityId,
    })),
    updated: mutations.updated.map((m) => ({
      container: m.spaceId,
      kind: "space" as const,
      prev: m.prev as DrawingEntity,
      next: m.next as DrawingEntity,
    })),
  };
}
